import { useEffect, useMemo, useState } from 'react';

import {
  Content,
  ContentHeader,
  CreateButton,
  SupportButton,
  type SupportItem,
  type TableColumn,
  useSupportConfig,
} from '@backstage/core-components';
import { CatalogTable, type CatalogTableRow } from '@backstage/plugin-catalog';
import {
  CatalogFilterLayout,
  EntityKindFilter,
  EntityRefLink,
  useEntityList,
} from '@backstage/plugin-catalog-react';

import Box from '@mui/material/Box';
import MemoryIcon from '@mui/icons-material/Memory';

import {
  DAAS_APPLICATION_SET_TYPE,
  DAAS_APPLICATION_TYPE,
  type DaaSContentProps,
  type DaaSType,
} from '../../types';
import { DaaSFilters } from './DaaSCustomFilters';
import { DaaSTypeTabFilter } from './DaaSTypeTabFilter';
import { DaaSTypeTabs } from './DaaSTypeTabs';
import { useDaaSEntityActions } from './EntityActions';
import { useStyles } from './styles';
import { DAAS_TYPE_TABS } from './typeTabs';

const DAAS_DOCS_URL = '/docs/default/repository/deployment-as-a-service';
const SUPPORT_CHANNEL_URL =
  'https://tomtomslack.slack.com/archives/C0746A4T0B1';

function getSupportItems(items: SupportItem[]): SupportItem[] {
  return [
    {
      title: 'DaaS documentation',
      icon: 'docs',
      links: [
        {
          title: 'Deployment as a Service',
          url: DAAS_DOCS_URL,
        },
      ],
    },
    {
      title: 'Support channel',
      icon: 'chat',
      links: [
        {
          title: '#pe-kaap',
          url: SUPPORT_CHANNEL_URL,
        },
      ],
    },
    ...items,
  ];
}

const ProjectCell = ({ entity }: { entity: CatalogTableRow['entity'] }) => {
  const { classes } = useStyles();
  const projectId = entity.spec?.projectId as string | undefined;
  const projectName = entity.spec?.projectName as string | undefined;

  if (!projectId) return <>{projectName ?? '—'}</>;

  return (
    <Box className={classes.projectCell}>
      <MemoryIcon fontSize="inherit" className={classes.projectCellIcon} />
      <EntityRefLink
        entityRef={`component:default/${projectId}`}
        defaultKind="component"
      >
        {projectName ?? projectId}
      </EntityRefLink>
    </Box>
  );
};

const createColumnsForType = (
  kind: string,
  type: DaaSType,
): TableColumn<CatalogTableRow>[] => {
  const autoWidth = (column: TableColumn<CatalogTableRow>) => ({
    ...column,
    width: 'auto',
  });

  const projectColumn: TableColumn<CatalogTableRow> = {
    title: 'Project',
    field: 'entity.spec.projectName',
    width: 'auto',
    render: ({ entity }) => <ProjectCell entity={entity} />,
  };

  const baseColumns = [
    CatalogTable.columns.createTitleColumn({ hidden: true }),
    autoWidth(CatalogTable.columns.createNameColumn({ defaultKind: kind })),
    autoWidth(CatalogTable.columns.createMetadataDescriptionColumn()),
  ];
  const ownerColumn = autoWidth(CatalogTable.columns.createOwnerColumn());

  switch (type) {
    case DAAS_APPLICATION_TYPE:
    case DAAS_APPLICATION_SET_TYPE:
      return [...baseColumns, projectColumn, ownerColumn];
    default:
      return [...baseColumns, ownerColumn];
  }
};

export const DaaSContent = (props: DaaSContentProps) => {
  const { initialKindFilter, title } = props;

  const globalSupportConfig = useSupportConfig();
  const actions = useDaaSEntityActions();

  const {
    updateFilters,
    queryParameters: { type: typeParameter },
  } = useEntityList();

  const [selectedType, setSelectedType] = useState<DaaSType>(() => {
    const queried = [typeParameter].flat().filter(Boolean) as string[];
    return (
      DAAS_TYPE_TABS.find(tab => queried.includes(tab.type))?.type ??
      DAAS_TYPE_TABS[0].type
    );
  });
  const selectedTab =
    DAAS_TYPE_TABS.find(tab => tab.type === selectedType) ?? DAAS_TYPE_TABS[0];

  useEffect(() => {
    updateFilters({
      kind: new EntityKindFilter(initialKindFilter, initialKindFilter),
    });
  }, [initialKindFilter, updateFilters]);

  useEffect(() => {
    updateFilters({ type: new DaaSTypeTabFilter(selectedType) });
  }, [selectedType, updateFilters]);

  const columns = useMemo(
    () => createColumnsForType(initialKindFilter, selectedType),
    [initialKindFilter, selectedType],
  );

  return (
    <>
      <Content>
				<ContentHeader>
					<CreateButton
            title={selectedTab.createButtonLabel}
            to={`/create/templates/default/${selectedTab.type}`}
          />
					<SupportButton items={getSupportItems(globalSupportConfig.items)}>
	          {title}
	        </SupportButton>
        </ContentHeader>
        <CatalogFilterLayout>
          <CatalogFilterLayout.Filters>
            <DaaSFilters />
          </CatalogFilterLayout.Filters>
          <CatalogFilterLayout.Content>
            <DaaSTypeTabs
              selectedType={selectedType}
              onChange={setSelectedType}
            />
            <CatalogTable
              columns={columns}
              actions={actions}
              tableOptions={{
                showTitle: false,
                actionsCellStyle: {
                  minWidth: 120,
                  maxWidth: 160,
									justifyItems: 'center',
                },
              }}
            />
          </CatalogFilterLayout.Content>
        </CatalogFilterLayout>
      </Content>
    </>
  );
};
